import { Link } from 'react-router-dom';
import PortfolioCoverageSummaryRail from '../common/PortfolioCoverageSummaryRail';
import { usePortfolioCoverageSummary } from '../../hooks/usePortfolioCoverageSummary';
import type { CoverageProofLine, PortfolioCoverageSummary } from '../../lib/portfolioCoverageSummary';

interface AIDataConnectionPanelProps {
  userId: string;
  featureName: string;
  valueStatement: string;
  className?: string;
}

function getSourceLabel(line: CoverageProofLine): string {
  switch (line.key) {
    case 'wallets':
      return 'wallet connections';
    case 'csv':
      return 'CSV imports';
    case 'exchanges':
      return line.status === 'stale' ? 'stale exchange syncs' : 'exchange syncs';
  }
}

function joinLabels(lines: CoverageProofLine[]): string {
  const labels = lines.map(getSourceLabel);

  if (labels.length <= 1) {
    return labels[0] ?? 'portfolio sources';
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

function getCoverageAction(
  action: PortfolioCoverageSummary['action'],
): { label: string; to: string } | null {
  if (!action) {
    return null;
  }

  switch (action.kind) {
    case 'review-wallets':
      return { label: 'Review Wallets', to: '/wallet-hub' };
    case 'review-import':
      return { label: 'Review Import', to: '/import' };
    case 'retry-sync':
    case 'manage-exchange':
      return { label: 'Open Exchange Sync', to: '/exchange-sync' };
    case 'review-source':
    case 'review-coverage':
      return { label: action.label, to: action.href };
    case 'review-portfolio':
    case 'inspect-analytics':
      return { label: 'Review Portfolio Analytics', to: '/portfolio-analytics' };
  }
}

export default function AIDataConnectionPanel({
  userId,
  featureName,
  valueStatement,
  className = '',
}: AIDataConnectionPanelProps) {
  const summary = usePortfolioCoverageSummary(userId, { surface: 'setup' });
  const lines = summary.lines ?? [];
  const includedSources = lines.filter((line) => line.status === 'included');
  const attentionSources = lines.filter((line) => line.status === 'stale' || line.status === 'unproven');
  const isCheckingCoverage =
    lines.length > 0 &&
    lines.every((line) => line.status === 'unproven' && line.meta.toLowerCase().startsWith('checking'));
  const actionLink = getCoverageAction(summary.action);

  const statusCopy = isCheckingCoverage
    ? {
        title: 'Checking your portfolio data connections',
        message:
          'Pinecurl is checking which saved wallets, exchange syncs, and imports this account can use for AI analysis.',
        toneClasses: 'border-primary-500/30 bg-primary-500/10',
      }
    : includedSources.length === 0
      ? {
          title: `${featureName} needs connected portfolio data`,
          message: `${featureName} can only ${valueStatement} after Pinecurl sees real portfolio sources. Connect a wallet, sync an exchange, or import a CSV below — this is a portfolio data issue, not a billing issue.`,
          toneClasses: 'border-amber-500/30 bg-amber-500/10',
        }
      : attentionSources.length > 0
        ? {
            title: `${featureName} can only see part of your portfolio`,
            message: `${featureName} can already use ${joinLabels(includedSources)}. Add or refresh ${joinLabels(attentionSources)} if you want it to ${valueStatement} across more of your holdings and transactions.`,
            toneClasses: 'border-amber-500/30 bg-amber-500/10',
          }
        : {
            title: `${featureName} is connected to your portfolio sources`,
            message: `${featureName} is already grounded in ${joinLabels(includedSources)}, so it can ${valueStatement} with the connected data shown below.`,
            toneClasses: 'border-emerald-500/30 bg-emerald-500/10',
          };

  return (
    <div className={`space-y-4 ${className}`.trim()}>
      <PortfolioCoverageSummaryRail
        summary={summary}
        action={
          actionLink ? (
            <Link to={actionLink.to} className="btn btn--primary text-sm">
              {actionLink.label}
            </Link>
          ) : undefined
        }
      />

      <div className={`rounded-xl border p-5 ${statusCopy.toneClasses}`}>
        <div className="space-y-3">
          <div>
            <p className="text-sm font-semibold text-text-primary">{statusCopy.title}</p>
            <p className="mt-1 text-sm text-text-secondary">{statusCopy.message}</p>
          </div>

          <p className="text-xs text-text-muted">
            Plan access and portfolio data are separate: your subscription unlocks AI tools, but connected
            wallets, exchange syncs, and imports determine what the AI can actually analyse.
          </p>

          <div className="flex flex-wrap gap-3">
            <Link to="/wallet-hub" className="btn btn--primary text-sm">
              Connect Wallet
            </Link>
            <Link to="/exchange-sync" className="btn btn--outline text-sm">
              Sync Exchange
            </Link>
            <Link to="/import" className="btn btn--ghost text-sm">
              Import CSV
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
